import { useState } from 'react'
import { Save } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { toast } from 'sonner'
import api from '@/services/api'
import type { Deal } from '@/types'

export function ObservacoesSection({
  deal,
  onDealPatched,
}: {
  deal: Deal
  onDealPatched: (updates: Partial<Deal>) => void
}) {
  const [editing, setEditing] = useState(false)
  const [editData, setEditData] = useState({
    observacoes: deal.observacoes ?? '',
    objecoes: deal.objecoes ?? '',
    concorrente: deal.concorrente ?? '',
  })
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      await api.patch(`/deals/${deal.id}`, editData)
      onDealPatched(editData as Partial<Deal>)
      setEditing(false)
      toast.success('Deal atualizado')
    } catch {
      toast.error('Erro ao salvar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="glass-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-bold text-accent uppercase tracking-wider">
          Observações
        </h3>
        <button
          onClick={() => {
            if (editing) {
              setEditing(false)
            } else {
              setEditData({
                observacoes: deal.observacoes ?? '',
                objecoes: deal.objecoes ?? '',
                concorrente: deal.concorrente ?? '',
              })
              setEditing(true)
            }
          }}
          className="text-[10px] text-accent hover:underline cursor-pointer"
        >
          {editing ? 'Cancelar' : 'Editar'}
        </button>
      </div>

      {editing ? (
        <div className="space-y-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">
              Observações
            </label>
            <textarea
              value={editData.observacoes}
              onChange={(e) => setEditData({ ...editData, observacoes: e.target.value })}
              rows={3}
              className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-sm rounded-lg px-3 py-2 outline-none focus:border-accent resize-none"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">
              Objeções
            </label>
            <textarea
              value={editData.objecoes}
              onChange={(e) => setEditData({ ...editData, objecoes: e.target.value })}
              rows={2}
              className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-sm rounded-lg px-3 py-2 outline-none focus:border-accent resize-none"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">
              Concorrente
            </label>
            <input
              value={editData.concorrente}
              onChange={(e) => setEditData({ ...editData, concorrente: e.target.value })}
              className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-sm rounded-lg px-3 py-2 outline-none focus:border-accent"
            />
          </div>
          <Button variant="primary" size="sm" loading={saving} onClick={handleSave} icon={<Save size={13} />}>
            Salvar
          </Button>
        </div>
      ) : (
        <>
          {deal.observacoes && <p className="text-sm text-text-secondary">{deal.observacoes}</p>}
          {deal.objecoes && (
            <>
              <h4 className="text-xs font-medium text-text-label uppercase tracking-wider">Objeções</h4>
              <p className="text-sm text-text-secondary">{deal.objecoes}</p>
            </>
          )}
          {!deal.observacoes && !deal.objecoes && (
            <p className="text-sm text-text-label italic">Nenhuma observação registrada.</p>
          )}
        </>
      )}
    </div>
  )
}
